import type { EngagementNextAction } from '@/__generated__/graphql';

/**
 * ENG-5452 / FE6 — wire shape → the customer roll-up's rows and filter options.
 *
 * The prototype's `components/CustomerEngagementsRollup/derive.ts` is NOT
 * reused: it derives from `EngagementScene`, and `viewModel.ts` documents why
 * that adapter cannot exist. Its filtering also moved server-side, where the
 * scope lives. What is left here is projection and the option lists.
 */

/**
 * The status pills, mapped to the exact lifecycle states the api's `status`
 * facet takes.
 *
 * Deliberately the same five groupings as `EngagementFeedBucket`, so the
 * customer roll-up and the staff feed cannot describe the platform's lifecycle
 * two different ways. It is a local map rather than that enum because
 * `accountEngagements` takes exact statuses, not a bucket: the grouping has to
 * be spelled out somewhere, and spelling it here keeps it next to the labels.
 *
 * `closed` covers `canceled`, because to a customer both are simply over.
 * `undefined` is "no filter", which the api reads as every state including the
 * terminal ones.
 */
export interface RollupStatusFilter {
  key: string;
  label: string;
  statuses: string[] | undefined;
}

export const ROLLUP_STATUS_FILTERS: RollupStatusFilter[] = [
  { key: 'all', label: 'All', statuses: undefined },
  { key: 'pending', label: 'Pending', statuses: ['pending_offer', 'pending_confirmation'] },
  { key: 'active', label: 'Active', statuses: ['active'] },
  { key: 'closing', label: 'Closing', statuses: ['closing'] },
  { key: 'closed', label: 'Closed', statuses: ['closed', 'canceled'] },
];

/**
 * The created-range facet, as whole days back from now.
 *
 * Only the lower bound is offered. `createdBefore` exists on the api but a
 * two-ended range needs two date inputs to be worth anything, and "engagements
 * opened since" is the question a power user reviewing an account actually asks.
 */
export interface RollupCreatedRange {
  key: string;
  label: string;
  days: number | null;
}

export const ROLLUP_CREATED_RANGES: RollupCreatedRange[] = [
  { key: 'any', label: 'Any time', days: null },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: '12m', label: 'Last 12 months', days: 365 },
];

/** `days` → the RFC3339 lower bound the api wants, or null for no bound. */
export function createdAfterIso(days: number | null, now: Date): string | null {
  if (days === null) return null;
  const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  return from.toISOString();
}

export interface AccountRollupRow {
  engagementId: string;
  projectName: string;
  projectId: string | null;
  expertName: string;
  expertHasProfile: boolean;
  status: string;
  awaiting: string | null;
  nextAction: EngagementNextAction;
  hasPendingAction: boolean;
  nteConsumed: number | null;
  nteTotal: number | null;
  hasOffer: boolean;
  createdAtIso: string;
}

/** The placeholder these surfaces use for an absent value. */
const NONE = '—';

function toHours(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function fromAccountEngagements(
  edges: readonly unknown[] | null | undefined
): AccountRollupRow[] {
  return (edges ?? [])
    .map((edge) => (edge as { node?: Record<string, any> } | null)?.node)
    .filter((node): node is Record<string, any> => Boolean(node))
    .map((node): AccountRollupRow => {
      const total = toHours(node.nteHoursTotal);
      const consumed = toHours(node.nteHoursConsumed);
      // No ceiling means no offer yet, which is a different thing from a
      // ceiling of zero: the NTE cell must read "—", not "0h / 0h".
      const hasOffer = total !== null && total > 0;

      return {
        engagementId: String(node.id),
        // `primaryProject` is `project:view`-filtered and null for a viewer who
        // holds the account but not that project. `primaryProjectName` is the
        // ENG-5612 display-only field that survives it, so the name renders
        // either way and only the option to filter by it is withheld.
        projectName: node.primaryProject?.name ?? node.primaryProjectName ?? NONE,
        projectId: node.primaryProject?.id ? String(node.primaryProject.id) : null,
        expertName: node.expertIdentity?.name ?? 'Awaiting expert',
        expertHasProfile: Boolean(node.expertIdentity?.userId),
        status: node.status,
        awaiting: node.awaiting ?? null,
        nextAction: node.nextAction,
        hasPendingAction: Boolean(node.hasPendingAction),
        nteConsumed: consumed,
        nteTotal: total,
        hasOffer,
        createdAtIso: String(node.createdAt),
      };
    });
}

export interface RollupFacetOption {
  id: string;
  name: string;
}

export interface RollupFacets {
  experts: RollupFacetOption[];
  projects: RollupFacetOption[];
}

/**
 * The unfiltered page → the expert and project picker options.
 *
 * Deduplicated by id and sorted by name, so the pickers read as a directory
 * rather than as the order engagements happened to be created in. A row whose
 * project the viewer cannot see contributes no project option: filtering by an
 * id that resolves to nothing would return an empty page and read as a bug.
 */
export function facetOptions(edges: readonly unknown[] | null | undefined): RollupFacets {
  const experts = new Map<string, string>();
  const projects = new Map<string, string>();

  for (const edge of edges ?? []) {
    const node = (edge as { node?: Record<string, any> } | null)?.node;
    if (!node) continue;
    const expertId = node.expertIdentity?.userId;
    if (expertId) experts.set(String(expertId), node.expertIdentity?.name ?? NONE);
    const projectId = node.primaryProject?.id;
    if (projectId) projects.set(String(projectId), node.primaryProject?.name ?? NONE);
  }

  const byName = (a: RollupFacetOption, b: RollupFacetOption) => a.name.localeCompare(b.name);
  const toOptions = (source: Map<string, string>) =>
    Array.from(source, ([id, name]) => ({ id, name })).sort(byName);

  return { experts: toOptions(experts), projects: toOptions(projects) };
}

/** The four filter controls, as one value so the bar takes one prop. */
export interface RollupFilterState {
  statusKey: string;
  expertId: string;
  projectId: string;
  rangeKey: string;
}

export const DEFAULT_ROLLUP_FILTERS: RollupFilterState = {
  statusKey: 'all',
  expertId: '',
  projectId: '',
  rangeKey: 'any',
};

/**
 * Filter state → the query's variables.
 *
 * Empty string means "not filtering" in the controls and `null` means it on the
 * wire, so the mapping lives here rather than inline at the call site: an empty
 * string sent as an `ID` would filter for an expert whose id is the empty string
 * and return a permanently empty page.
 */
export function rollupQueryVariables(
  filters: RollupFilterState,
  now: Date
): {
  status: string[] | undefined;
  expertId: string | null;
  projectId: string | null;
  createdAfter: string | null;
} {
  const days = ROLLUP_CREATED_RANGES.find((r) => r.key === filters.rangeKey)?.days ?? null;
  return {
    status: ROLLUP_STATUS_FILTERS.find((f) => f.key === filters.statusKey)?.statuses,
    expertId: filters.expertId || null,
    projectId: filters.projectId || null,
    createdAfter: createdAfterIso(days, now),
  };
}

/** Identity of a filter set, for guarding a `fetchMore` that lands late. */
export function rollupFilterKey(filters: RollupFilterState): string {
  return [filters.statusKey, filters.expertId, filters.projectId, filters.rangeKey].join('|');
}
